import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  IconPlus,
  IconSearch,
  IconMessage2,
  IconAlertCircle,
  IconRefresh,
  IconInfoCircle,
} from '@tabler/icons-react';
import styles from './Feedbacks.module.scss';
import {
  useGetFeedback,
  usePostFeedback,
  getGetFeedbackQueryKey,
} from '../../api/feedback-api/feedback-api';
import type { FeedbackTicketDto, CreateFeedbackRequest } from '../../api/model';
import {
  STATUS_COLOR,
  PRIORITY_COLOR,
  PRIORITY_WEIGHT,
  STATUS_VALUES,
  TYPE_VALUES,
  PRIORITY_VALUES,
  TYPE_ICON,
} from './feedbackMeta';
import { formatRelative, ticketRef } from './feedbackFormat';
import FeedbackDetail from './FeedbackDetail';
import CreateFeedbackDrawer from './CreateFeedbackDrawer';

interface FeedbacksProps {
  headerSlot?: React.ReactNode;
  initialTicketId?: number | null;
  isSystemAdmin?: boolean;
}

type SortKey = 'recent' | 'created' | 'priority';

const tint = (hex: string) => `${hex}1a`;

const activityTime = (tk: FeedbackTicketDto): number => {
  const v = tk.lastActivityAt || tk.modified || tk.created;
  return v ? new Date(v).getTime() : 0;
};

const Feedbacks: React.FC<FeedbacksProps> = ({ headerSlot, initialTicketId, isSystemAdmin }) => {
  const { t, i18n } = useTranslation('feedback');
  const locale = i18n.language;
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useGetFeedback({ query: { enabled: !isSystemAdmin } });
  const tickets = useMemo<FeedbackTicketDto[]>(() => data ?? [], [data]);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortKey>('recent');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (initialTicketId != null) setSelectedId(initialTicketId);
  }, [initialTicketId]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: getGetFeedbackQueryKey() });

  const createMutation = usePostFeedback({
    mutation: {
      onSuccess: (created) => {
        invalidate();
        setDrawerOpen(false);
        if (created?.id !== undefined) setSelectedId(created.id);
      },
      onError: () => setCreateError(t('errors.createFailed')),
    },
  });

  const filtered = useMemo<FeedbackTicketDto[]>(() => {
    const term = searchTerm.trim().toLowerCase();
    let result = tickets.filter((tk) => {
      if (term) {
        const haystack = [tk.title, tk.description, tk.reporterName, ticketRef(tk.id)]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (statusFilter && tk.status !== statusFilter) return false;
      if (typeFilter && tk.type !== typeFilter) return false;
      if (priorityFilter && tk.priority !== priorityFilter) return false;
      return true;
    });

    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'created':
          return (b.created ? new Date(b.created).getTime() : 0) - (a.created ? new Date(a.created).getTime() : 0);
        case 'priority': {
          const pa = a.priority ? PRIORITY_WEIGHT[a.priority] : 0;
          const pb = b.priority ? PRIORITY_WEIGHT[b.priority] : 0;
          if (pb !== pa) return pb - pa;
          return activityTime(b) - activityTime(a);
        }
        case 'recent':
        default:
          return activityTime(b) - activityTime(a);
      }
    });

    return result;
  }, [tickets, searchTerm, statusFilter, typeFilter, priorityFilter, sortBy]);

  const selected = useMemo(
    () => (selectedId !== null ? tickets.find((tk) => tk.id === selectedId) ?? null : null),
    [tickets, selectedId],
  );

  // Keep the last opened ticket around
  const [renderTicket, setRenderTicket] = useState<FeedbackTicketDto | null>(null);
  useEffect(() => {
    if (selected) setRenderTicket(selected);
  }, [selected]);

  if (isSystemAdmin) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>{t('header.title')}</h1>
            <p className={styles.subtitle}>{t('header.subtitle')}</p>
          </div>
          <div className={styles.headerActions}>{headerSlot}</div>
        </header>
        <div className={styles.adminNotice}>
          <div className={styles.adminNoticeIcon}>
            <IconInfoCircle size={34} />
          </div>
          <h3>{t('admin.title')}</h3>
          <p>{t('admin.body')}</p>
        </div>
      </div>
    );
  }

  const hasFilters = !!statusFilter || !!typeFilter || !!priorityFilter || !!searchTerm.trim();

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setTypeFilter('');
    setPriorityFilter('');
  };

  const handleSelect = (id?: number) => {
    if (id === undefined) return;
    setSelectedId(id);
  };

  const handleCreate = (payload: CreateFeedbackRequest) => {
    setCreateError(null);
    createMutation.mutate({ data: payload });
  };

  const openDrawer = () => {
    setCreateError(null);
    setDrawerOpen(true);
  };

  const renderList = () => {
    if (isLoading) {
      return <div className={styles.stateBox}>{t('list.loading')}</div>;
    }
    if (isError) {
      return (
        <div className={styles.stateBox}>
          <IconAlertCircle size={28} className={styles.stateIcon} />
          <p>{t('list.loadError')}</p>
          <button className={styles.retryButton} onClick={() => refetch()} type="button">
            <IconRefresh size={16} />
            {t('actions.retry')}
          </button>
        </div>
      );
    }
    if (filtered.length === 0) {
      const noTickets = tickets.length === 0;
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <IconMessage2 size={36} />
          </div>
          <h3>{noTickets ? t('list.empty.title') : t('list.noMatch.title')}</h3>
          <p>{noTickets ? t('list.empty.body') : t('list.noMatch.body')}</p>
          {noTickets ? (
            <button className={styles.primaryButton} onClick={openDrawer} type="button">
              <IconPlus size={16} />
              {t('list.empty.cta')}
            </button>
          ) : (
            <button className={styles.linkButton} onClick={clearFilters} type="button">
              {t('filters.clear')}
            </button>
          )}
        </div>
      );
    }

    return filtered.map((tk) => {
      const statusColor = tk.status ? STATUS_COLOR[tk.status] : '#6b7280';
      const priorityColor = tk.priority ? PRIORITY_COLOR[tk.priority] : '#6b7280';
      const TypeIcon = tk.type ? TYPE_ICON[tk.type] : IconMessage2;
      const isActive = tk.id === selectedId;
      return (
        <button
          key={tk.id}
          type="button"
          className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
          style={{ borderLeftColor: statusColor }}
          onClick={() => handleSelect(tk.id)}
        >
          <span className={styles.rowRef}>{ticketRef(tk.id)}</span>
          <span className={styles.rowTitle}>{tk.title}</span>
          <span className={styles.rowType}>
            <TypeIcon size={14} />
            {tk.type ? t(`types.${tk.type}`) : '—'}
          </span>
          {tk.priority && (
            <span
              className={styles.rowPriority}
              style={{ color: priorityColor, backgroundColor: tint(priorityColor) }}
            >
              {t(`priorities.${tk.priority}`)}
            </span>
          )}
          {tk.status && (
            <span className={styles.rowStatus} style={{ color: statusColor }}>
              {t(`statuses.${tk.status}`)}
            </span>
          )}
          <span className={styles.rowTime}>{formatRelative(tk.lastActivityAt || tk.created, locale)}</span>
        </button>
      );
    });
  };

  const listCount = filtered.length;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>{t('header.title')}</h1>
          <p className={styles.subtitle}>{t('header.subtitle')}</p>
        </div>
        <div className={styles.headerActions}>
          {headerSlot}
          <button className={styles.primaryButton} onClick={openDrawer} type="button">
            <IconPlus size={16} />
            {t('actions.new')}
          </button>
        </div>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.searchInput}>
          <IconSearch size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('search.placeholder')}
          />
        </div>

        <div className={styles.selects}>
          <select
            className={styles.select}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label={t('filters.status')}
          >
            <option value="">{t('filters.status')}</option>
            {STATUS_VALUES.map((status) => (
              <option key={status} value={status}>
                {t(`statuses.${status}`)}
              </option>
            ))}
          </select>

          <select
            className={styles.select}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label={t('filters.type')}
          >
            <option value="">{t('filters.type')}</option>
            {TYPE_VALUES.map((type) => (
              <option key={type} value={type}>
                {t(`types.${type}`)}
              </option>
            ))}
          </select>

          <select
            className={styles.select}
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            aria-label={t('filters.priority')}
          >
            <option value="">{t('filters.priority')}</option>
            {PRIORITY_VALUES.map((priority) => (
              <option key={priority} value={priority}>
                {t(`priorities.${priority}`)}
              </option>
            ))}
          </select>

          <select
            className={styles.select}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortKey)}
            aria-label={t('filters.sortBy')}
          >
            <option value="recent">{t('sort.recent')}</option>
            <option value="created">{t('sort.created')}</option>
            <option value="priority">{t('sort.priority')}</option>
          </select>

          {hasFilters && (
            <button className={styles.linkButton} onClick={clearFilters} type="button">
              {t('filters.clear')}
            </button>
          )}
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.listCount}>{t('list.count', { count: listCount })}</div>
        <div className={styles.listScroll}>{renderList()}</div>
      </div>

      <FeedbackDetail
        ticketId={selectedId}
        fallbackTicket={selected ?? renderTicket}
        isOpen={selected !== null}
        onClose={() => setSelectedId(null)}
      />

      <CreateFeedbackDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleCreate}
        isSubmitting={createMutation.isPending}
        error={createError}
      />
    </div>
  );
};

export default Feedbacks;
